import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Trash2, ExternalLink } from 'lucide-react';
import { fulfillmentApi, type FulfillmentCatalog, type FulfillmentProvider } from '../../api/endpoints/fulfillment';
import { metafieldsApi } from '../../api/endpoints/metafields';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { useToast } from '../../components/ui/Alert';
import type { CustomField } from '../../types/product';

interface Props {
  storeId: string;
  productId: string;
  fields: CustomField[];
}

/** Flatten a provider catalog into SKU options for the searchable select. */
function useCatalogSkuOptions(catalog?: FulfillmentCatalog) {
  return useMemo(() => {
    if (!catalog) return [];
    const opts: { value: string; label: string }[] = [];
    for (const cat of catalog.categories || []) {
      for (const p of cat.products || []) {
        const name = p.title || p.name || p.external_id;
        opts.push({ value: p.external_id, label: `${cat.title} · ${name} (${p.external_id})` });
      }
    }
    return opts;
  }, [catalog]);
}

/**
 * Generic, provider-agnostic product fulfillment mapping. Reads the store's
 * installed sourcing apps (providers), lets the merchant pick a SKU from the
 * provider's catalog, and maps each of the provider's declared inputs (uid,
 * region, ...) to one of THIS product's buyer-input fields. Everything is driven
 * by the provider's `inputs` spec, so no supplier is hardcoded. The mapping is
 * saved as a metafield in the provider's namespace (appSlug).
 */
export function AppFulfillmentSection({ storeId, productId, fields }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: providers = [], isLoading: providersLoading } = useQuery({
    queryKey: ['fulfillment-providers', storeId],
    queryFn: () => fulfillmentApi.providers(storeId),
    enabled: !!storeId,
  });

  const [selectedInstallationId, setSelectedInstallationId] = useState('');
  useEffect(() => {
    if (providers.length > 0 && !providers.some((p) => p.installationId === selectedInstallationId)) {
      setSelectedInstallationId(providers[0].installationId);
    }
  }, [providers, selectedInstallationId]);

  const provider: FulfillmentProvider | undefined = providers.find(
    (p) => p.installationId === selectedInstallationId,
  );
  const namespace = provider?.appSlug ?? '';

  const { data: catalog, isLoading: catalogLoading } = useQuery({
    queryKey: ['fulfillment-catalog', storeId, selectedInstallationId],
    queryFn: () => fulfillmentApi.catalog(storeId, selectedInstallationId),
    enabled: !!storeId && !!selectedInstallationId && !!provider?.hasCatalog,
  });
  const skuOptions = useCatalogSkuOptions(catalog);

  const { data: metafields } = useQuery({
    queryKey: ['product-metafields', storeId, productId, namespace],
    queryFn: () => metafieldsApi.get(storeId, productId, namespace),
    enabled: !!storeId && !!productId && !!namespace,
  });

  // Local editing state, prefilled from the saved metafield whenever the
  // provider changes or the metafield reloads.
  const [externalId, setExternalId] = useState('');
  const [inputMap, setInputMap] = useState<Record<string, string>>({});
  useEffect(() => {
    const mf = (metafields?.metafields ?? {}) as Record<string, unknown>;
    setExternalId(mf.externalId != null ? String(mf.externalId) : '');
    const next: Record<string, string> = {};
    for (const inp of provider?.inputs ?? []) {
      next[inp.key] = mf[inp.key] != null ? String(mf[inp.key]) : '';
    }
    setInputMap(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metafields, selectedInstallationId]);

  const hasMapping = !!metafields?.metafields && Object.keys(metafields.metafields).length > 0;

  const fieldOptions = fields.map((f) => ({ value: String(f.id), label: f.title }));

  const requiredInputsFilled = (provider?.inputs ?? [])
    .filter((inp) => inp.required)
    .every((inp) => !!inputMap[inp.key]);
  const canSave = !!provider && !!externalId && requiredInputsFilled;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['product-metafields', storeId, productId, namespace] });
    queryClient.invalidateQueries({ queryKey: ['product', storeId, productId] });
    queryClient.invalidateQueries({ queryKey: ['products', storeId] });
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { externalId };
      for (const inp of provider?.inputs ?? []) {
        // Only include a key when the merchant chose a field for it (required
        // ones are guaranteed by canSave; optionals stay out when empty).
        if (inputMap[inp.key]) body[inp.key] = inputMap[inp.key];
      }
      return metafieldsApi.put(storeId, productId, namespace, body);
    },
    onSuccess: () => { invalidate(); toast('success', t('fulfillment.saved', 'Fulfillment mapping saved')); },
    onError: () => toast('error', t('common.error')),
  });

  const removeMutation = useMutation({
    mutationFn: () => metafieldsApi.remove(storeId, productId, namespace),
    onSuccess: () => {
      invalidate();
      setExternalId('');
      setInputMap({});
      toast('success', t('fulfillment.removed', 'Fulfillment mapping removed'));
    },
    onError: () => toast('error', t('common.error')),
  });

  if (providersLoading) {
    return <div className="py-4"><LoadingSpinner /></div>;
  }

  // No installed fulfillment app: a subtle hint only, nothing distracting.
  if (providers.length === 0) {
    return (
      <button
        type="button"
        onClick={() => navigate(`/${storeId}/apps`)}
        className="mt-3 flex items-center gap-1.5 text-xs text-text-muted hover:text-accent transition-colors"
      >
        <ExternalLink className="h-3.5 w-3.5" />
        {t('fulfillment.installHint', 'Install a fulfillment app to source this product')}
      </button>
    );
  }

  return (
    <div className="mt-3 space-y-3">
      {providers.length > 1 && (
        <Select
          label={t('fulfillment.provider', 'Fulfillment app')}
          options={providers.map((p) => ({ value: p.installationId, label: p.appName }))}
          value={selectedInstallationId}
          onChange={(e) => setSelectedInstallationId(e.target.value)}
        />
      )}

      {fields.length === 0 ? (
        <div className="flex items-start gap-2 rounded-lg border border-border-default bg-bg-tertiary px-3 py-2.5">
          <AlertTriangle className="h-4 w-4 text-text-muted shrink-0 mt-0.5" />
          <p className="text-xs text-text-secondary">
            {t('fulfillment.noFieldsHint', 'Add a buyer-input field to this product first, so the app can capture what it needs from the buyer.')}
          </p>
        </div>
      ) : (
        <PermissionGate
          requires="products.edit"
          fallback={
            <p className="text-xs text-text-muted">{t('fulfillment.noPermission', 'You do not have permission to edit fulfillment mapping.')}</p>
          }
        >
          <div>
            <label className="mb-1.5 block text-sm text-text-secondary">
              {t('fulfillment.sku', 'App SKU')}
            </label>
            {catalogLoading ? (
              <div className="flex items-center gap-2 rounded-lg border border-border-default bg-bg-field px-3 py-2.5 text-sm text-text-muted">
                <LoadingSpinner /> {t('fulfillment.loadingCatalog', 'Loading app catalog...')}
              </div>
            ) : (
              <SearchableSelect
                options={skuOptions}
                value={externalId || null}
                onChange={(v) => setExternalId(v == null ? '' : String(v))}
                placeholder={t('fulfillment.selectSku', 'Search and select an app SKU')}
              />
            )}
          </div>

          {(provider?.inputs ?? []).map((inp) => (
            <Select
              key={inp.key}
              label={inp.required ? inp.label : t('fulfillment.optionalInput', '{{label}} (optional)', { label: inp.label })}
              options={[
                { value: '', label: inp.required ? t('fulfillment.selectField', 'Select a field') : t('fulfillment.none', 'None') },
                ...fieldOptions,
              ]}
              value={inputMap[inp.key] ?? ''}
              onChange={(e) => setInputMap((prev) => ({ ...prev, [inp.key]: e.target.value }))}
            />
          ))}

          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => saveMutation.mutate()}
              isLoading={saveMutation.isPending}
              disabled={!canSave}
            >
              {t('fulfillment.saveMapping', 'Save mapping')}
            </Button>
            {hasMapping && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="border-danger/30 text-danger hover:bg-danger/5"
                onClick={() => removeMutation.mutate()}
                isLoading={removeMutation.isPending}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {t('fulfillment.remove', 'Remove')}
              </Button>
            )}
          </div>
        </PermissionGate>
      )}
    </div>
  );
}
